import {ChangeSet, type ChangeSpec, EditorSelection, Text} from '@codemirror/state'
import type {Command} from '@codemirror/view'
import type {List, Root} from 'mdast'

import {buildItemContinuationSyntax} from '../../../language-tools/markdown/continuation-utils'
import {buildFirstMarkerSyntax, buildNextMarkerSyntax} from '../../../language-tools/markdown/list-utils'
import {MarkdownLine} from '../../../language-tools/markdown/markdown-line'
import {
  getChildrenOverlappingRange,
  getNodesContainingRange,
} from '../../../language-tools/syntax-tree/node-range-utils'
import {isPositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getMarkdownTree} from '../../extensions/markdown-parser'
import {removeList} from './remove-list'
import {positionedItems} from './utils'

/**
 * Finds the position of the end of the blockquote prefix at the given `depth`, where `depth` is the number of
 * blockquote ancestors. For example, if the line is `> > > > hello` and the depth is `2`, will return `3` (the index
 * after the location of the second '>' prefix).
 */
function findBlockquotePrefixEnd(line: MarkdownLine, depth: number) {
  if (depth === 0) return line.start
  for (const prefix of line.prefixes)
    if (prefix.tokenType === 'blockQuotePrefix') {
      depth--
      if (depth === 0) return prefix.position?.end.offset ?? line.start
    }
  return line.start
}

/** Generate changes to convert an entire list to a different type. Does not actually execute the transaction. */
function buildListConversion(tree: Root, doc: string, list: List, toOrdered: boolean): ChangeSpec[] {
  const changes: ChangeSpec[] = []

  let lastMarker: string | null = null

  for (const item of positionedItems(list.children)) {
    const lines = MarkdownLine.linesAtRange(tree, doc, item.position.start.offset, item.position.end.offset - 1)
    for (const [i, line] of lines.entries()) {
      if (i === 0) {
        const prefix = line?.prefixes.find(p => p.tokenType === 'listItemPrefix')
        if (!prefix || !isPositionedNode(prefix)) continue
        const marker: string = lastMarker ? buildNextMarkerSyntax(lastMarker) : buildFirstMarkerSyntax(toOrdered)
        changes.push({
          from: prefix.position.start.offset,
          to: prefix.position.end.offset,
          insert: marker,
        })
        lastMarker = marker
      } else {
        // If the length of the marker has changed, we need to also change the amount of trailing indent that all
        // the remaining lines have
        const indentPrefix = line?.prefixes.find(p => p.tokenType === 'listItemIndent')
        if (
          !indentPrefix ||
          !lastMarker ||
          !isPositionedNode(indentPrefix) ||
          indentPrefix.raw.length === lastMarker.length
        )
          continue
        changes.push({
          from: indentPrefix.position.start.offset,
          to: indentPrefix.position.end.offset,
          insert: buildItemContinuationSyntax(lastMarker),
        })
      }
    }
  }
  return changes
}

/**
 * Either create, replace, or remove lists depending on the selection and contents.
 */
const formatList =
  (ordered: boolean): Command =>
  (view, eventSource?: 'keyboard' | 'toolbar') => {
    const state = view.state
    const tree = getMarkdownTree(state)
    const doc = state.doc.toString()
    const selection = state.selection.main
    const selectionCollapsed = selection.from === selection.to
    const caretLine = selectionCollapsed ? MarkdownLine.atOffset(tree, doc, selection.from) : null
    const userEvent = eventSource === 'keyboard' ? 'input.type' : 'input'

    // If the selection is fully contained inside of a matching list, this is a remove or replace operation:
    const deepestListContainingSelection = Array.from(
      getNodesContainingRange(tree, selection.from, selection.to, true),
    ).findLast(n => n.type === 'list')
    if (deepestListContainingSelection) {
      if ((deepestListContainingSelection.ordered ?? false) !== ordered) {
        // Non-matching list: replace it with the new type
        const changes = buildListConversion(tree, doc, deepestListContainingSelection, ordered)
        view.dispatch({
          changes,
          scrollIntoView: true,
          userEvent,
        })
        return true
      } else {
        return removeList(view)
      }
    }

    // Otherwise, create lists around all the selected content while converting any lists inside the selection:

    /** Array of top-level affected nodes to operate on. */
    let selectedNodes = getChildrenOverlappingRange(tree, selection.from, selection.to)
    /** Track the number of wrapping blockquote elements, so we can determine where on a line to insert the list marker. */
    let blockquoteDepth = 0

    // If selection is fully inside a blockquote, dig into it rather than making the whole quote one list item.
    // Blockquotes are the only case we handle here for now. There's an argument to make for taking a similar approach
    // for HTML (particularly details/summary), but that gets more complicated since the <details> tag could be on the
    // same line as the content, and then where do you put the marker? I'd rather hold off on that until asked for it.
    while (selectedNodes.length === 1 && selectedNodes[0]!.type === 'blockquote') {
      selectedNodes = getChildrenOverlappingRange(selectedNodes[0], selection.from, selection.to)
      blockquoteDepth++
    }

    const caretBlockquoteDepth = caretLine
      ? caretLine.prefixes.filter(prefix => prefix.tokenType === 'blockQuotePrefix').length
      : 0
    if (caretBlockquoteDepth > blockquoteDepth) {
      // When selection is empty, the caret might be inside additional nested blockquotes that weren't preserved
      // in selectedNodes (e.g. the caret is on a blank line inside a deeper quote). Use the caret depth so that
      // list markers land after the correct number of blockquote prefixes.
      blockquoteDepth = caretBlockquoteDepth
    }

    let lastMarker: string | null = null
    const changes: ChangeSpec[] = []
    let explicitSelection: EditorSelection | undefined

    // Gather lines per top-level node so we can treat paragraphs (multiple list items) differently from block nodes
    // (single item + hanging indents) while preserving processing order.
    const lineGroups: Array<{lines: MarkdownLine[]; nodeType: string}> = []

    for (const node of selectedNodes) {
      // If the node is a list, convert that entire list (even items outside the selection) to the new type
      if (node.type === 'list') {
        changes.push(...buildListConversion(tree, doc, node, ordered))
        continue
      }

      const nodeLines = isPositionedNode(node)
        ? MarkdownLine.linesAtRange(tree, doc, node.position.start.offset, node.position.end.offset - 1)
        : []

      if (nodeLines.length > 0) {
        lineGroups.push({lines: nodeLines, nodeType: node.type})
      }
    }

    if (lineGroups.length === 0 && caretLine) {
      lineGroups.push({lines: [caretLine], nodeType: 'paragraph'})
    }

    const totalGroups = lineGroups.length

    for (const [groupIndex, group] of lineGroups.entries()) {
      const {lines, nodeType} = group

      for (const [lineIndex, line] of lines.entries()) {
        const overlapsSelection =
          line.overlaps(selection.from, selection.to) ||
          (selectionCollapsed && (selection.from === line.start || selection.from === line.end))

        // Blank lines don't get a marker or indentation, unless the caret specifically targets them
        if (line.isBlank && !overlapsSelection) continue

        // Normally the insertion point would be the beginning of the line, but inside a blockquote it will go after the
        // blockquote prefix(es)
        const from = findBlockquotePrefixEnd(line, blockquoteDepth)

        // In paragraphs, ignore lines that don't overlap the selection
        if (nodeType === 'paragraph' && !overlapsSelection) continue

        // For paragraphs, every line becomes a new list item (allowing easily creating a list from several adjacent
        // lines). For all other node types (blocks), we only create a single item by applying the prefix to first
        // line and adding hanging indent (continuation syntax) to the remaining lines
        if (lineIndex === 0 || nodeType === 'paragraph') {
          const prevLine = line.previous()
          const prevLineIsBlank = prevLine && prevLine.isBlank
          // Before the start of a list, ensure the previous line is blank
          if (lastMarker === null && prevLine && !prevLine.isBlank && !line.isBlank) {
            changes.push({
              from: line.start - 1,
              insert: '\n',
            })
          }

          // If the previous line is blank, restart numbering
          const marker: string =
            lastMarker === null || prevLineIsBlank ? buildFirstMarkerSyntax(ordered) : buildNextMarkerSyntax(lastMarker)

          const markerChange: ChangeSpec = {
            from,
            insert: marker,
          }

          changes.push(markerChange)

          lastMarker = marker
        } else if (lastMarker) {
          changes.push({
            from,
            insert: buildItemContinuationSyntax(lastMarker),
          })
        }

        // On the last line of the last node, insert a blank line if necessary
        if (!line.isBlank && groupIndex === totalGroups - 1 && lineIndex === lines.length - 1) {
          const nextLine = line.next()
          if (nextLine && !nextLine.isBlank) {
            changes.push({
              from: line.end,
              insert: '\n',
            })
          }
        }
      }
    }

    if (changes.length === 0) {
      // The selection may be a collapsed caret on a truly blank line. In that case the syntax tree does not expose a
      // node to operate on, so synthesize the list marker directly on the line and preserve surrounding spacing.
      const line = MarkdownLine.atOffset(tree, doc, selection.from)
      if (line) {
        const caretWithinLine = selectionCollapsed && (selection.from === line.start || selection.from === line.end)
        if (caretWithinLine) {
          const blockquotePrefixDepth = line.prefixes.filter(prefix => prefix.tokenType === 'blockQuotePrefix').length
          const insertionPoint = findBlockquotePrefixEnd(line, blockquoteDepth || blockquotePrefixDepth)

          const prevLine = line.previous()
          const newlineBeforeInserted = prevLine && !prevLine.isBlank ? 1 : 0
          if (newlineBeforeInserted) {
            changes.push({
              from: line.start - 1,
              insert: '\n',
            })
          }

          const marker = buildFirstMarkerSyntax(ordered)
          changes.push({
            from: insertionPoint,
            insert: marker,
          })

          if (!explicitSelection) {
            const anchor = insertionPoint + newlineBeforeInserted + marker.length
            explicitSelection = EditorSelection.single(anchor, anchor)
          }

          const nextLine = line.next()
          if (nextLine && !nextLine.isBlank) {
            changes.push({
              from: line.end,
              insert: '\n',
            })
          }
        }
      }

      if (changes.length === 0) {
        return false
      }
    }

    if (!explicitSelection && selectionCollapsed && changes.length > 0) {
      try {
        const changeSet = ChangeSet.of(changes, doc.length)
        if (caretLine?.isBlank) {
          const anchor = changeSet.mapPos(selection.from, 1)
          explicitSelection = EditorSelection.single(anchor)
        } else {
          const updatedDoc = changeSet.apply(Text.of(doc.split('\n'))).toString()
          const insertedMarker = buildFirstMarkerSyntax(ordered)
          const searchStart = Math.max(0, changeSet.mapPos(selection.from, -1) - insertedMarker.length)
          let markerIndex = updatedDoc.indexOf(insertedMarker, searchStart)
          if (markerIndex === -1) {
            markerIndex = updatedDoc.indexOf(insertedMarker)
          }

          const newlineMarkerIndex = updatedDoc.indexOf(`\n${insertedMarker}`, Math.max(0, selection.from - 1))
          if (newlineMarkerIndex !== -1 && (markerIndex === -1 || newlineMarkerIndex + 1 < markerIndex)) {
            markerIndex = newlineMarkerIndex + 1
          } else if (markerIndex !== -1 && updatedDoc.charAt(markerIndex - 1) !== '\n') {
            const fallbackNewlineIndex = updatedDoc.indexOf(`\n${insertedMarker}`, Math.max(0, selection.from - 1))
            if (fallbackNewlineIndex !== -1) {
              markerIndex = fallbackNewlineIndex + 1
            }
          }

          let anchor = changeSet.mapPos(selection.from, 1)
          if (markerIndex >= 0 && anchor < markerIndex + insertedMarker.length) {
            anchor = markerIndex + insertedMarker.length
          }
          if (markerIndex >= 0) {
            const markerNewlineIndex = updatedDoc.indexOf(`\n${insertedMarker}`, Math.max(0, markerIndex - 1))
            if (markerNewlineIndex !== -1) {
              const newlineAnchorTarget = markerNewlineIndex + 1 + insertedMarker.length
              if (anchor < newlineAnchorTarget) {
                anchor = newlineAnchorTarget
              }
            }
          }

          const safeAnchor = Math.min(anchor, updatedDoc.length)
          explicitSelection = EditorSelection.single(safeAnchor, safeAnchor)
        }
      } catch {
        // If changes are invalid for ChangeSet (should not happen), fall back to default behavior without overriding.
      }
    }

    const transaction: Parameters<typeof view.dispatch>[0] = {
      changes,
      scrollIntoView: true,
      userEvent,
    }
    if (explicitSelection) {
      transaction.selection = explicitSelection
    }

    view.dispatch(transaction)
    return true
  }

export const formatUnorderedList: Command = formatList(false)

export const formatOrderedList: Command = formatList(true)
